import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { railsToIana, convertScheduleWithDays, findNextRaceSlot, findSlotForDate } from '../utils/timezoneUtils';
import { RMC_CONFIG, RMC_TRAINERS, RMC_POWER_CATEGORIES, RMC_AGE_CATEGORIES_MEN, RMC_AGE_CATEGORIES_WOMEN, getUserAgeCategory } from '../constants/rmc';
import CountryDisplay from './CountryDisplay';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { formatTimeFromDate, formatClockString, formatNumericDate } from '../utils/timeFormat';
import { downloadFile, readBlobError } from '../utils/fileDownload';
import './RMCDashboard.css';

const RMCDashboard = () => {
  const { user, updateUser } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [joinMessage, setJoinMessage] = useState('');
  const [joinTrainer, setJoinTrainer] = useState('');
  const [joinCategory, setJoinCategory] = useState('');
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [joinSuccess, setJoinSuccess] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);

  const [requestStatus, setRequestStatus] = useState(null); // null | 'pending' | 'approved' | 'rejected'
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requestStats, setRequestStats] = useState(null);
  const [currentStage, setCurrentStage] = useState(null);
  const [wildCards, setWildCards] = useState(null);
  const [loading, setLoading] = useState(!!user);

  const [rouvySyncRequired, setRouvySyncRequired] = useState(false);

  const [acceptingRules, setAcceptingRules] = useState(false);
  const [catTab, setCatTab] = useState('power');

  const [participantsPdfLoading, setParticipantsPdfLoading] = useState(false);
  const [participantsPdfError, setParticipantsPdfError] = useState('');

  const [leadersCategory, setLeadersCategory] = useState('');
  const [leadersGender, setLeadersGender] = useState(user?.gender || '');
  const [leadersAgeCategory, setLeadersAgeCategory] = useState('');
  const [leaders, setLeaders] = useState([]);
  const [leadersLoading, setLeadersLoading] = useState(!!user);
  // Группа мощности приходит только с дашбордом. Пока ее нет, запрос лидеров не уходит:
  // иначе на экране сначала появлялся список по всем группам и потом подменялся нужным
  const [leadersFiltersReady, setLeadersFiltersReady] = useState(false);
  // Номер последнего запроса: ответы приходят не в том порядке, в котором ушли,
  // и запоздавший ответ по всем группам затирал уже показанный список своей группы
  const leadersRequestRef = useRef(0);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      setLeadersLoading(false);
      return;
    }
    // Смена пользователя: старая категория больше не годится, ждем новый дашборд
    setLeadersFiltersReady(false);
    setLeadersLoading(true);
    axios.get('/api/rmc/dashboard')
      .then(res => {
        setRequestStatus(res.data.join_request_status);
        setRulesAccepted(res.data.rmc_rules_accepted);
        setIsAdmin(res.data.admin);
        if (res.data.request_stats) setRequestStats(res.data.request_stats);
        if (res.data.current_stage) setCurrentStage(res.data.current_stage);
        if (res.data.wild_cards) setWildCards(res.data.wild_cards);
        if (res.data.join_request_power_category) setLeadersCategory(res.data.join_request_power_category);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        // Категория известна (или ее нет) — только теперь фильтры окончательные
        setLeadersFiltersReady(true);
      });
  }, [user]);

  // Подсказка про синхронизацию нужна только тем, у кого ROUVY не подключен вовсе
  // или у кого токен выдан без нужных прав — им придется авторизоваться заново
  useEffect(() => {
    if (!user?.id) {
      setRouvySyncRequired(false);
      return;
    }

    let cancelled = false;
    axios.get('/api/rouvy/status')
      .then(res => {
        if (cancelled) return;
        setRouvySyncRequired(!res.data.connected || Boolean(res.data.needs_reauthorization));
      })
      .catch(() => { if (!cancelled) setRouvySyncRequired(false); });

    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (user?.gender) setLeadersGender(user.gender);
  }, [user?.gender]);

  useEffect(() => {
    if (!user?.id || !leadersFiltersReady) return;

    const requestId = leadersRequestRef.current + 1;
    leadersRequestRef.current = requestId;
    // Ответ на отмененный фильтр игнорируем: пока он летел, пользователь мог выбрать другой
    const isCurrent = () => leadersRequestRef.current === requestId;

    setLeadersLoading(true);
    const params = new URLSearchParams();
    if (leadersCategory) params.append('power_category', leadersCategory);
    if (leadersGender) params.append('gender', leadersGender);
    if (leadersAgeCategory) params.append('age_category', leadersAgeCategory);

    axios.get(`/api/rmc/leaders?${params}`)
      .then(res => { if (isCurrent()) setLeaders(res.data); })
      .catch(() => { if (isCurrent()) setLeaders([]); })
      .finally(() => { if (isCurrent()) setLeadersLoading(false); });
  }, [leadersCategory, leadersGender, leadersAgeCategory, leadersFiltersReady, user?.id]);

  // Возрастные категории у мужчин и женщин свои: выбран пол — показываем только
  // его категории, не выбран — обе группы
  const leadersAgeOptions = useMemo(() => {
    const men = { key: 'male', label: t('rmc.dashboard.leadersGenderMale'), cats: RMC_AGE_CATEGORIES_MEN };
    const women = { key: 'female', label: t('rmc.dashboard.leadersGenderFemale'), cats: RMC_AGE_CATEGORIES_WOMEN };
    if (leadersGender === 'male') return [men];
    if (leadersGender === 'female') return [women];
    return [men, women];
  }, [leadersGender, t]);

  // Сменили пол — категория другого пола теряет смысл, иначе список был бы пуст
  const handleLeadersGenderChange = (gender) => {
    setLeadersGender(gender);
    const ageCats = gender === 'female' ? RMC_AGE_CATEGORIES_WOMEN : RMC_AGE_CATEGORIES_MEN;
    if (gender && leadersAgeCategory && !ageCats.some(c => c.cat === leadersAgeCategory)) {
      setLeadersAgeCategory('');
    }
  };

  const formatAgeRange = ({ minAge, maxAge }) => (maxAge === null ? `${minAge}+` : `${minAge}–${maxAge}`);

  const formatRideTime = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    const time = formatTimeFromDate(date, language);
    const dayOfWeek = t('common.days')[date.getDay()];
    return `${dayOfWeek}, ${formatNumericDate(date, language)}, ${time}`;
  };

  const schedule = useMemo(() => {
    const targetZone = railsToIana(user?.time_zone);
    const { slots, days, tzAbbr } = convertScheduleWithDays(
      RMC_CONFIG.schedule,
      RMC_CONFIG.dowMap,
      RMC_CONFIG.sourceTimezone,
      targetZone,
      RMC_CONFIG.referenceDate,
    );
    const fallbackNextSlot = findNextRaceSlot(
      RMC_CONFIG.schedule,
      RMC_CONFIG.dowMap,
      RMC_CONFIG.sourceTimezone,
    );
    return { slots, days, tzAbbr, fallbackNextSlot };
  }, [user?.time_zone]);

  const handleJoinRequest = async () => {
    if (!joinTrainer) {
      setJoinError(t('rmc.join.trainerRequired'));
      return;
    }
    if (!joinCategory) {
      setJoinError(t('rmc.join.categoryRequired'));
      return;
    }
    setJoinLoading(true);
    setJoinError('');
    try {
      await axios.post('/api/rmc/join_requests', {
        message: joinMessage,
        trainer: joinTrainer,
        power_category: joinCategory,
      });
      setJoinSuccess(true);
      setRequestStatus('pending');
    } catch (err) {
      const msg = err.response?.data?.error || t('rmc.join.error');
      if (msg.includes('pending')) {
        setJoinError(t('rmc.join.alreadyPending'));
        setRequestStatus('pending');
      } else {
        setJoinError(msg);
      }
    } finally {
      setJoinLoading(false);
    }
  };

  const handleAcceptRules = async () => {
    setAcceptingRules(true);
    try {
      await axios.post('/api/rmc/accept_rules');
      updateUser({ ...user, rmc_rules_accepted: true });
      setRulesAccepted(true);
    } catch (err) {
      console.error('Failed to accept rules', err);
    } finally {
      setAcceptingRules(false);
    }
  };

  // Язык интерфейса уезжает на бэкенд: подписи и имена в документе печатаются на нем же
  const handleParticipantsPdf = async () => {
    setParticipantsPdfLoading(true);
    setParticipantsPdfError('');
    try {
      await downloadFile('/api/rmc/participants_pdf', {
        params: { language },
        fallbackName: 'rmc-participants.pdf'
      });
    } catch (err) {
      console.error('Failed to download participants PDF', err);
      const details = await readBlobError(err);
      setParticipantsPdfError([t('rmc.dashboard.participantsPdfError'), details].filter(Boolean).join(': '));
    } finally {
      setParticipantsPdfLoading(false);
    }
  };

  // --- Shared UI blocks ---

  const bannerBlock = (active = false) => (
    <div className={`rmc-header-banner ${active ? 'rmc-header-active' : ''}`}>
      <div className="rmc-banner-content">
        <h1>Rouvy Mamiky's Championship</h1>
        <p className="rmc-season">{t('rmc.season')}</p>
        <p className="rmc-period">📅 {t('rmc.period')}</p>
        {requestStatus === 'approved' && rulesAccepted && (
          <span className="rmc-member-badge">✅ {t('rmc.access.alreadyMember')}</span>
        )}
      </div>
    </div>
  );

  const infoBlock = () => (
    <div className="rmc-info-card">
      <p className="rmc-description">{t('rmc.description')}</p>
      <Link to="/rmc/rules" className="rmc-rules-link">
        📄 {t('rmc.rulesLink')}
      </Link>
    </div>
  );

  const scheduleCard = (highlightNext = false) => {
    const nextSlot = highlightNext
      ? (currentStage?.next_ride?.start_date_time_utc
          ? findSlotForDate(
              new Date(currentStage.next_ride.start_date_time_utc),
              RMC_CONFIG.schedule,
              RMC_CONFIG.dowMap,
              RMC_CONFIG.sourceTimezone,
            ) || schedule.fallbackNextSlot
          : schedule.fallbackNextSlot)
      : null;

    return (
      <div className="rmc-card">
        <h3>🗓 {t('rmc.dashboard.schedule')}</h3>
        <div className="rmc-schedule-block">
          {schedule.days.map(dow => {
            const daySlots = schedule.slots.filter(s => s.localDow === dow);
            return (
              <div key={dow} className="rmc-schedule-day">
                <span className="rmc-schedule-day-label">{t('common.days')[dow]}</span>
                <span className="rmc-schedule-times">
                  {daySlots.map((slot, i) => (
                    <React.Fragment key={`${slot.sourceDayKey}-${slot.sourceTimeIndex}`}>
                      {i > 0 && ', '}
                      <span className={
                        nextSlot &&
                        nextSlot.dayKey === slot.sourceDayKey &&
                        nextSlot.timeIndex === slot.sourceTimeIndex
                          ? 'rmc-time-next'
                          : ''
                      }>{formatClockString(slot.localTime, language)}</span>
                    </React.Fragment>
                  ))}
                </span>
              </div>
            );
          })}
          <span className="rmc-schedule-tz">{schedule.tzAbbr}</span>
        </div>
      </div>
    );
  };

  const currentStageCard = () => {
    if (!currentStage) return null;

    return (
      <div className="rmc-stage-card">
        <h3>🚴 {t('rmc.dashboard.currentStage')}</h3>
        <Link to={`/events/${currentStage.event_id}`} className="rmc-stage-link">
          {currentStage.thumbnail ? (
            <img src={currentStage.thumbnail} alt={currentStage.name} className="rmc-stage-thumbnail" />
          ) : (
            <div className="rmc-stage-thumbnail-placeholder">🚴</div>
          )}
          <span className="rmc-stage-text">
            <span className="rmc-stage-name">{currentStage.name}</span>
            {currentStage.route_name && (
              <span className="rmc-stage-route-name">{currentStage.route_name}</span>
            )}
          </span>
        </Link>
        {currentStage.next_ride?.rouvy_link && (
          <a
            href={currentStage.next_ride.rouvy_link}
            target="_blank"
            rel="noopener noreferrer"
            className="rmc-stage-ride-link"
            onClick={(e) => e.stopPropagation()}
          >
            ⏱ {formatRideTime(currentStage.next_ride.start_date_time_utc)}
          </a>
        )}
      </div>
    );
  };

  const categoriesCard = () => {
    const gender = user?.gender;
    const age = user?.age;
    const userAgeCat = getUserAgeCategory(age, gender);
    const ageCats = gender === 'female' ? RMC_AGE_CATEGORIES_WOMEN : RMC_AGE_CATEGORIES_MEN;

    return (
      <div className="rmc-card">
        <h3>⚡ {t('rmc.dashboard.categories')}</h3>

        <div className="rmc-cat-tabs">
          <button
            className={`rmc-cat-tab${catTab === 'power' ? ' rmc-cat-tab-active' : ''}`}
            onClick={() => setCatTab('power')}
          >
            {t('rmc.dashboard.catTabPower')}
          </button>
          <button
            className={`rmc-cat-tab${catTab === 'age' ? ' rmc-cat-tab-active' : ''}`}
            onClick={() => setCatTab('age')}
          >
            {t('rmc.dashboard.catTabAge')}
          </button>
        </div>

        {catTab === 'power' && (
          <>
            <p className="rmc-cat-desc">{t('rmc.dashboard.categoriesDesc')}</p>
            <div className="rmc-category-list">
              {RMC_POWER_CATEGORIES.map(({ cat, wkg }) => (
                <span key={cat} className="rmc-cat-badge">{cat} {wkg}</span>
              ))}
            </div>
          </>
        )}

        {catTab === 'age' && (
          <>
            <p className="rmc-cat-desc">
              {gender === 'female' ? t('rmc.dashboard.ageCategoriesWomen') : t('rmc.dashboard.ageCategoriesMen')}
            </p>
            <div className="rmc-category-list">
              {ageCats.map(({ cat, minAge, maxAge }) => (
                <span
                  key={cat}
                  className={`rmc-cat-badge${userAgeCat?.cat === cat ? ' rmc-cat-active' : ''}`}
                >
                  {cat} {maxAge === null ? `${minAge}+` : `${minAge}–${maxAge}`}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const leadersCard = () => (
    <div className="rmc-leaders-card">
      <h3>🏆 {t('rmc.dashboard.leaders')}</h3>

      <div className="rmc-leaders-filters">
        <div className="rmc-leaders-filter-group">
          <label>{t('rmc.dashboard.leadersFilterPower')}</label>
          <select
            value={leadersCategory}
            onChange={e => setLeadersCategory(e.target.value)}
            className="rmc-select rmc-leaders-select"
          >
            <option value="">{t('rmc.join.categoryPlaceholder')}</option>
            {RMC_POWER_CATEGORIES.map(({ cat, wkg }) => (
              <option key={cat} value={cat}>{cat} ({wkg} W/kg)</option>
            ))}
          </select>
        </div>
        <div className="rmc-leaders-filter-group">
          <label>{t('rmc.dashboard.leadersFilterGender')}</label>
          <select
            value={leadersGender}
            onChange={e => handleLeadersGenderChange(e.target.value)}
            className="rmc-select rmc-leaders-select"
          >
            <option value="">{t('rmc.dashboard.leadersGenderAll')}</option>
            <option value="male">{t('rmc.dashboard.leadersGenderMale')}</option>
            <option value="female">{t('rmc.dashboard.leadersGenderFemale')}</option>
          </select>
        </div>
        <div className="rmc-leaders-filter-group">
          <label>{t('rmc.dashboard.leadersFilterAge')}</label>
          <select
            value={leadersAgeCategory}
            onChange={e => setLeadersAgeCategory(e.target.value)}
            className="rmc-select rmc-leaders-select"
          >
            <option value="">{t('rmc.join.categoryPlaceholder')}</option>
            {leadersAgeOptions.length === 1
              ? leadersAgeOptions[0].cats.map(c => (
                  <option key={c.cat} value={c.cat}>{c.cat} ({formatAgeRange(c)})</option>
                ))
              : leadersAgeOptions.map(group => (
                  <optgroup key={group.key} label={group.label}>
                    {group.cats.map(c => (
                      <option key={c.cat} value={c.cat}>{c.cat} ({formatAgeRange(c)})</option>
                    ))}
                  </optgroup>
                ))}
          </select>
        </div>
      </div>

      <p className="rmc-leaders-note">ℹ️ {t('rmc.dashboard.leadersNote')}</p>

      {leadersLoading ? (
        <div className="rmc-leaders-empty">...</div>
      ) : leaders.length === 0 ? (
        <div className="rmc-leaders-empty">{t('rmc.dashboard.leadersNoData')}</div>
      ) : (
        <div className="rmc-leaders-grid">
          {leaders.map(l => (
            <div key={l.user_id} className={`rmc-leader-item${l.user_id === user?.id ? ' rmc-leader-me' : ''}`}>
              <div className="rmc-leader-pos">#{l.position}</div>
              <img
                src={getUserAvatarUrl(l.avatar)}
                alt={l.nickname}
                className="rmc-leader-avatar"
                onError={handleUserAvatarError}
              />
              <div className="rmc-leader-info">
                <span className="rmc-leader-name">
                  {language !== 'ru' && l.first_name_eng ? `${l.first_name_eng} ${l.last_name_eng}` : `${l.first_name} ${l.last_name}`}
                </span>
                <span className="rmc-leader-nick">@{l.nickname}</span>
                <span className="rmc-leader-country">
                  {l.country && <CountryDisplay country={l.country} language={language} />}
                </span>
              </div>
              <span className="rmc-leader-wkg">{l.wkg} W/kg</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const joinForm = (disabled = false) => (
    <div className="rmc-join-form">
      <div className="form-group">
        <label>{t('rmc.join.trainerLabel')}</label>
        <select
          value={disabled ? '' : joinTrainer}
          onChange={(e) => setJoinTrainer(e.target.value)}
          className="rmc-select"
          disabled={disabled}
        >
          <option value="">{t('rmc.join.trainerPlaceholder')}</option>
          <optgroup label={t('rmc.join.trainerClassA')}>
            {RMC_TRAINERS.classA.map(({ group, models }) =>
              models.map((model) => (
                <option key={`${group} ${model}`} value={`${group} ${model}`}>
                  {group} {model}
                </option>
              ))
            )}
          </optgroup>
          <optgroup label={t('rmc.join.trainerClassB')}>
            {RMC_TRAINERS.classB.map(({ group, models }) =>
              models.map((model) => (
                <option key={`${group} ${model}`} value={`${group} ${model}`}>
                  {group} {model}
                </option>
              ))
            )}
          </optgroup>
        </select>
      </div>
      <div className="form-group">
        <label>{t('rmc.join.categoryLabel')}</label>
        <select
          value={disabled ? '' : joinCategory}
          onChange={(e) => setJoinCategory(e.target.value)}
          className="rmc-select"
          disabled={disabled}
        >
          <option value="">{t('rmc.join.categoryPlaceholder')}</option>
          {RMC_POWER_CATEGORIES.map(({ cat, wkg }) => (
            <option key={cat} value={cat}>
              {cat} ({wkg} W/kg)
            </option>
          ))}
        </select>
      </div>
      {!disabled && (
        <div className="form-group">
          <label>{t('rmc.join.messageLabel')}</label>
          <textarea
            value={joinMessage}
            onChange={(e) => setJoinMessage(e.target.value)}
            placeholder={t('rmc.join.messagePlaceholder')}
            rows={3}
            className="rmc-textarea"
          />
        </div>
      )}
      {joinError && <div className="error-message">{joinError}</div>}
      {disabled ? (
        <button
          className="btn btn-primary rmc-join-btn"
          onClick={() => navigate('/login', { state: { from: '/rmc' } })}
        >
          {t('rmc.access.loginButton')}
        </button>
      ) : (
        <button
          className="btn btn-primary rmc-join-btn"
          onClick={handleJoinRequest}
          disabled={joinLoading}
        >
          {joinLoading ? t('rmc.join.submitting') : t('rmc.join.submit')}
        </button>
      )}
    </div>
  );

  // --- Loading ---
  if (loading) return null;

  // --- State 0: Not logged in ---
  if (!user) {
    return (
      <div className="rmc-page">
        {bannerBlock()}
        <div className="rmc-content">
          {infoBlock()}
          <div className="rmc-cards-grid">
            {scheduleCard()}
            {categoriesCard()}
          </div>
          <div className="rmc-no-access-card">
            <h2>{t('rmc.join.title')}</h2>
            <p>{t('rmc.access.loginRequiredDesc')}</p>
            {joinForm(true)}
          </div>
        </div>
      </div>
    );
  }

  // --- State 0: Access was revoked by an organizer ---
  if (!isAdmin && requestStatus === 'revoked') {
    return (
      <div className="rmc-page">
        {bannerBlock()}
        <div className="rmc-content">
          {infoBlock()}
          <div className="rmc-no-access-card">
            <div className="rmc-lock-icon">🔒</div>
            <h2>{t('rmc.access.revoked')}</h2>
            <p>{t('rmc.access.revokedDesc')}</p>
          </div>
        </div>
      </div>
    );
  }

  // --- State 1: No request yet (or rejected) — lock screen, then form on click ---
  if (!isAdmin && (!requestStatus || requestStatus === 'rejected')) {
    return (
      <div className="rmc-page">
        {bannerBlock()}
        <div className="rmc-content">
          {infoBlock()}
          <div className="rmc-no-access-card">
            {showJoinForm ? (
              <>
                <h3>{t('rmc.join.title')}</h3>
                {joinForm(false)}
              </>
            ) : (
              <>
                <div className="rmc-lock-icon">🔒</div>
                <h2>{t('rmc.access.noAccess')}</h2>
                <p>{t('rmc.access.noAccessDesc')}</p>
                <button
                  className="btn btn-primary rmc-join-btn"
                  onClick={() => setShowJoinForm(true)}
                >
                  {t('rmc.access.requestJoin')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- State 2: Pending request ---
  if (!isAdmin && requestStatus === 'pending') {
    return (
      <div className="rmc-page">
        {bannerBlock()}
        <div className="rmc-content">
          {infoBlock()}
          <div className="rmc-no-access-card">
            <div className="rmc-request-sent">
              <div className="rmc-check-icon">✅</div>
              <h3>{t('rmc.access.requestSent')}</h3>
              <p>{t('rmc.access.requestSentDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- State 3: Approved but rules not accepted ---
  if (requestStatus === 'approved' && !rulesAccepted) {
    return (
      <div className="rmc-page">
        {bannerBlock()}
        <div className="rmc-content">
          <div className="rmc-accept-rules-card">
            <div className="rmc-rules-icon">📋</div>
            <h2>{t('rmc.rules_acceptance.title')}</h2>
            <p>{t('rmc.rules_acceptance.desc')}</p>
            <p className="rmc-read-first">{t('rmc.rules_acceptance.readFirst')}</p>
            <Link to="/rmc/rules" className="rmc-rules-link rmc-rules-link-large" target="_blank" rel="noopener noreferrer">
              📄 {t('rmc.rulesLink')}
            </Link>
            <button
              className="btn btn-primary rmc-accept-btn"
              onClick={handleAcceptRules}
              disabled={acceptingRules}
            >
              {acceptingRules ? t('rmc.rules_acceptance.accepting') : t('rmc.rules_acceptance.accept')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- State 4: Full member (approved + rules accepted) or Admin ---
  return (
    <div className="rmc-page">
      {bannerBlock(true)}
      <div className="rmc-content">
        <div className="rmc-info-card">
          <div className="rmc-info-text">
            <p>{t('rmc.dashboard.active')}</p>
            {rouvySyncRequired && (
              <p className="rmc-sync-notice">
                {t('rmc.dashboard.syncNotice')}{' '}
                <Link to="/profile">{t('rmc.dashboard.syncNoticeLink')}</Link>
              </p>
            )}
            {participantsPdfError && <p className="rmc-pdf-error">{participantsPdfError}</p>}
          </div>
          <div className="rmc-info-actions">
            <button
              type="button"
              className="rmc-rules-link"
              onClick={handleParticipantsPdf}
              disabled={participantsPdfLoading}
            >
              📥 {participantsPdfLoading ? t('rmc.dashboard.participantsPdfLoading') : t('rmc.dashboard.participantsPdf')}
            </button>
            <Link to="/rmc/rules" className="rmc-rules-link">
              📄 {t('rmc.rulesLink')}
            </Link>
          </div>
        </div>

        <div className="rmc-cards-grid">
          {scheduleCard(true)}
          <div className="rmc-card">
            <h3>🃏 {t('rmc.dashboard.wildcards')}</h3>
            <p>{t('rmc.dashboard.wildcardsDesc')}</p>
            {wildCards && (
              <div className="rmc-wildcards-counter">
                <span className="rmc-wildcards-remaining">{wildCards.remaining}</span>
                <span className="rmc-wildcards-total">/ {wildCards.total}</span>
                <span className="rmc-wildcards-label">{t('rmc.dashboard.wildcardsRemaining')}</span>
              </div>
            )}
          </div>
          {categoriesCard()}
          {currentStageCard()}
        </div>

        {leadersCard()}

        {isAdmin && requestStats && (
          <div className="rmc-admin-stats-card">
            <h3>📊 {t('rmc.admin.requestStats')}</h3>
            <div className="rmc-stats-grid">
              <div className="rmc-stat rmc-stat-pending" onClick={() => navigate('/rmc/admin')}>
                <span className="rmc-stat-count">{requestStats.pending}</span>
                <span className="rmc-stat-label">{t('rmc.admin.statPending')}</span>
              </div>
              <div className="rmc-stat rmc-stat-approved">
                <span className="rmc-stat-count">{requestStats.approved}</span>
                <span className="rmc-stat-label">{t('rmc.admin.statApproved')}</span>
              </div>
              <div className="rmc-stat rmc-stat-rejected">
                <span className="rmc-stat-count">{requestStats.rejected}</span>
                <span className="rmc-stat-label">{t('rmc.admin.statRejected')}</span>
              </div>
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/rmc/admin')}
            >
              ⚙️ {t('rmc.admin.title')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RMCDashboard;
